import {
  format,
  formatDistanceToNow,
  addDays,
  differenceInDays,
  isPast,
  isWithinInterval,
  startOfDay,
  endOfDay
} from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { logger } from './logger';

interface TimestampLike {
  toDate(): Date;
  seconds?: number;
  _seconds?: number;
}

/**
 * Universal parser for various date/timestamp formats used in the app (Firestore, serialized, JS Date, ISO string)
 * 
 * @param timestamp - The date/timestamp to parse
 * @returns A JS Date object or null if parsing fails
 * 
 * @example
 * ```typescript
 * const date = parseToJSDate(firestoreTimestamp);
 * const dateFromIso = parseToJSDate("2023-01-01T00:00:00Z");
 * ```
 */
export function parseToJSDate(timestamp: Timestamp | Date | null | undefined | unknown): Date | null {
  if (timestamp === null || timestamp === undefined) return null;

  // Handle Firestore Timestamp objects
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  // Handle plain Date objects
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // Handle objects with toDate method (duck typing for Timestamp-like objects)
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp && typeof (timestamp as TimestampLike).toDate === 'function') {
    return (timestamp as TimestampLike).toDate();
  }
  // Handle serialized Firestore timestamps from HTTP responses (have _seconds property)
  if (typeof timestamp === 'object' && timestamp !== null && (timestamp as TimestampLike)._seconds !== undefined) {
    return new Date((timestamp as TimestampLike)._seconds! * 1000);
  }
  // Handle serialized timestamps with seconds property
  if (typeof timestamp === 'object' && timestamp !== null && (timestamp as TimestampLike).seconds !== undefined) {
    return new Date((timestamp as TimestampLike).seconds! * 1000);
  }
  // Handle ISO date strings or numeric timestamps
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * Reconstruct Firestore `Timestamp`s that were flattened to plain
 * `{ seconds, nanoseconds }` objects by the structured-clone algorithm.
 *
 * React Router stashes navigation state via `history.pushState`, which
 * structured-clones the state. A Firestore client `Timestamp` survives the
 * clone as its own enumerable data (`{ seconds, nanoseconds }`) but LOSES its
 * prototype — so `.toDate()` is gone. Any consumer that later calls `.toDate()`
 * on such a value throws `TypeError: x.toDate is not a function` (s26-1).
 *
 * This walks arrays and plain objects and rebuilds real `Timestamp`s in a fresh
 * copy, so a router-state-seeded document is indistinguishable from a live
 * Firestore read for every downstream consumer. Genuine `Timestamp`/`Date`
 * instances pass through untouched.
 */
function isFlattenedTimestamp(value: unknown): value is { seconds: number; nanoseconds: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { seconds?: unknown }).seconds === 'number' &&
    typeof (value as { nanoseconds?: unknown }).nanoseconds === 'number' &&
    typeof (value as { toDate?: unknown }).toDate !== 'function'
  );
}

export function reviveTimestamps<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (value instanceof Timestamp || value instanceof Date) return value;
  if (isFlattenedTimestamp(value)) {
    return new Timestamp(value.seconds, value.nanoseconds) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => reviveTimestamps(v)) as unknown as T;
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      out[key] = reviveTimestamps(v);
    }
    return out as T;
  }
  return value;
}

/**
 * Format a timestamp as MM/DD/YYYY
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted date string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatDate(timestamp); // "01/27/2026"
 * ```
 */
export function formatDate(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, 'MM/dd/yyyy');
  } catch (error: unknown) {
    logger.error('Error formatting date', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp as MM/DD/YYYY h:mm a
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted date/time string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatDateTime(timestamp); // "01/27/2026 2:30 PM"
 * ```
 */
export function formatDateTime(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, 'MM/dd/yyyy h:mm a');
  } catch (error: unknown) {
    logger.error('Error formatting datetime', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp as MMM d, yyyy (e.g., Jan 27, 2026)
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted date string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatDateText(timestamp); // "Jan 27, 2026"
 * ```
 */
export function formatDateText(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, 'MMM d, yyyy');
  } catch (error: unknown) {
    logger.error('Error formatting date text', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp as MMM d (e.g., Jan 27)
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted date string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatDateShortText(timestamp); // "Jan 27"
 * ```
 */
export function formatDateShortText(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, 'MMM d');
  } catch (error: unknown) {
    logger.error('Error formatting short date text', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp as EEEE, MMMM d, yyyy (e.g., Tuesday, January 27, 2026)
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted date string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatDateFull(timestamp); // "Tuesday, January 27, 2026"
 * ```
 */
export function formatDateFull(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, 'EEEE, MMMM d, yyyy');
  } catch (error: unknown) {
    logger.error('Error formatting full date', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp relative to now (e.g., "2 days ago")
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Relative time string or 'N/A' if null/undefined
 * 
 * @example
 * ```typescript
 * formatRelativeTime(timestamp); // "2 days ago"
 * ```
 */
export function formatRelativeTime(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error: unknown) {
    logger.error('Error formatting relative time', error, { timestamp });
    return 'Invalid date';
  }
}

/**
 * Format a timestamp for timeline display (e.g., "5m ago", "3h ago", "2d ago", or "Jan 27")
 * 
 * @param timestamp - The date/timestamp to format
 * @returns Formatted relative or short date string, or empty string if null/undefined
 * 
 * @example
 * ```typescript
 * formatTimelineRelative(timestamp); // "5m ago"
 * ```
 */
export function formatTimelineRelative(timestamp: Timestamp | Date | null | undefined | unknown): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return format(date, 'MMM d');
  } catch (error: unknown) {
    logger.error('Error formatting timeline relative time', error, { timestamp });
    return '';
  }
}

/**
 * Calculate next request date based on an interval
 * 
 * @param lastReceivedDate - The date of the last received record
 * @param intervalDays - Number of days between requests
 * @returns The next request date or null
 */
export function calculateNextRequestDate(
  lastReceivedDate: Timestamp | null,
  intervalDays: number
): Date | null {
  if (!lastReceivedDate) return null;

  try {
    const lastReceived = lastReceivedDate.toDate();
    return addDays(lastReceived, intervalDays);
  } catch (error: unknown) {
    logger.error('Error calculating next request date', error);
    return null;
  }
}

/**
 * Check if a request is due (next request date is today or in the past)
 * 
 * @param nextRequestDate - The scheduled next request date
 * @returns True if the request is due or overdue
 */
export function isRequestDue(nextRequestDate: Timestamp | null): boolean {
  if (!nextRequestDate) return false;

  try {
    const nextDate = nextRequestDate.toDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    return differenceInDays(today, nextDate) >= 0;
  } catch (error: unknown) {
    logger.error('Error checking if request is due', error);
    return false;
  }
}

/**
 * Check if a request is overdue (no response after expected time)
 * 
 * @param requestedAt - The date the request was sent
 * @param expectedResponseDays - Number of days allowed for response (default: 30)
 * @returns True if the request is past its expected response date
 */
export function isRequestOverdue(
  requestedAt: Timestamp | null,
  expectedResponseDays: number = 30
): boolean {
  if (!requestedAt) return false;

  try {
    const requested = requestedAt.toDate();
    const expectedResponseDate = addDays(requested, expectedResponseDays);
    return isPast(expectedResponseDate);
  } catch (error: unknown) {
    logger.error('Error checking if request is overdue', error);
    return false;
  }
}

/**
 * Get the number of days until the next request
 * 
 * @param nextRequestDate - The scheduled next request date
 * @returns Number of days until next request or null
 */
export function getDaysUntilNextRequest(nextRequestDate: Timestamp | null): number | null {
  if (!nextRequestDate) return null;

  try {
    const nextDate = nextRequestDate.toDate();
    const today = new Date();
    return differenceInDays(nextDate, today);
  } catch (error: unknown) {
    logger.error('Error getting days until next request', error);
    return null;
  }
}

/**
 * Convert a JS Date to a Firestore Timestamp
 * 
 * @param date - The Date to convert
 * @returns A Firestore Timestamp
 */
export function dateToTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

/**
 * Get the current time as a Firestore Timestamp
 * 
 * @returns Current Firestore Timestamp
 */
export function getCurrentTimestamp(): Timestamp {
  return Timestamp.now();
}

/**
 * Parse a date string in MM/DD/YYYY format to a JS Date
 * 
 * @param dateString - The string to parse
 * @returns A JS Date or null if invalid
 * 
 * @example
 * ```typescript
 * parseDateString("01/27/2026");
 * ```
 */
export function parseDateString(dateString: string): Date | null {
  const parts = dateString.split('/');

  if (parts.length !== 3) {
    return null;
  }

  const month = parseInt(parts[0], 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  const date = new Date(year, month, day);

  // Validate that the date is valid
  if (
    date.getMonth() !== month ||
    date.getDate() !== day ||
    date.getFullYear() !== year
  ) {
    return null;
  }

  return date;
}

/**
 * Parse a date-only string (no time component) to a LOCAL Date.
 *
 * Accepts `MM/DD/YYYY`, `YYYY-MM-DD`, and `MM-DD-YYYY`, all anchored to local
 * midnight. This is the correct parser for values that came from an
 * `<input type="date">` (YYYY-MM-DD) or an OCR/manual `MM/DD/YYYY` entry: parsing
 * the ISO form via `new Date("YYYY-MM-DD")` yields UTC midnight, which then renders
 * one calendar day earlier when formatDate() displays it in a UTC-negative zone (S20-1).
 *
 * Strings that carry an explicit time/zone (full ISO datetime) are passed through to
 * the native parser unchanged, since their instant is already unambiguous.
 *
 * @param dateStr - The date string to parse
 * @returns A JS Date at local midnight (for date-only input) or null if invalid
 */
export function parseDateOnly(dateStr: string): Date | null {
  if (!dateStr) return null;
  const cleaned = dateStr.trim();

  const mdy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(cleaned);
  if (mdy) {
    const date = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
    return isNaN(date.getTime()) ? null : date;
  }

  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(cleaned);
  if (ymd) {
    const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    return isNaN(date.getTime()) ? null : date;
  }

  // Full ISO datetime (or anything else): the instant is already unambiguous.
  const date = new Date(cleaned);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a timestamp using a custom format string
 * 
 * @param timestamp - The date/timestamp to format
 * @param formatStr - The date-fns format string to use
 * @returns Formatted date string or 'N/A' if null/undefined
 */
export function formatCustomDate(
  timestamp: Timestamp | Date | null | undefined | unknown,
  formatStr: string
): string {
  try {
    const date = parseToJSDate(timestamp);
    if (!date) return 'N/A';
    return format(date, formatStr);
  } catch (error: unknown) {
    logger.error('Error formatting custom date', error, { timestamp, formatStr });
    return 'Invalid date';
  }
}

/**
 * Get the number of days between two dates
 * 
 * @param dateLeft - The first date
 * @param dateRight - The second date
 * @returns Number of days between the two dates (dateLeft - dateRight)
 */
export function getDifferenceInDays(
  dateLeft: Timestamp | Date | string | number | null | undefined,
  dateRight: Timestamp | Date | string | number | null | undefined
): number {
  try {
    const left = parseToJSDate(dateLeft);
    const right = parseToJSDate(dateRight);
    if (!left || !right) return 0;
    return differenceInDays(left, right);
  } catch (error: unknown) {
    logger.error('Error calculating difference in days', error);
    return 0;
  }
}

/**
 * Get the number of days from now until a future date
 * 
 * @param futureDate - The future date
 * @returns Number of days until the future date (minimum 0)
 */
export function getDaysUntil(futureDate: Timestamp | Date | string | number | null | undefined): number {
  try {
    const date = parseToJSDate(futureDate);
    if (!date) return 0;
    return Math.max(0, differenceInDays(date, new Date()));
  } catch (error: unknown) {
    logger.error('Error calculating days until', error);
    return 0;
  }
}

/**
 * Check if a date is within a given interval
 * 
 * @param date - The date to check
 * @param interval - The interval to check against
 * @returns True if the date is within the interval
 */
export function isDateInInterval(
  date: Timestamp | Date | string | number | null | undefined,
  interval: { start?: string | Date | null; end?: string | Date | null }
): boolean {
  try {
    const targetDate = parseToJSDate(date);
    if (!targetDate) return false;

    // Helper to parse date string (YYYY-MM-DD) as local midnight
    const parseLocal = (d: unknown) => {
      if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
        const [y, m, day] = d.split('-').map(Number);
        return new Date(y, m - 1, day);
      }
      return parseToJSDate(d);
    };

    const start = interval.start ? startOfDay(parseLocal(interval.start) || new Date(0)) : new Date(0);
    const end = (interval.end ? endOfDay(parseLocal(interval.end) || new Date(8640000000000000)) : new Date(8640000000000000));

    return isWithinInterval(targetDate, { start, end });
  } catch (error: unknown) {
    logger.error('Error checking if date is in interval', error);
    return false;
  }
}
